/**
 * One symbol's analyst note, held for the page and re-run only on request.
 *
 * The same module-scope promise cache as `symbol-fundamentals.ts` and
 * `symbol-profile.ts`, with one rule the others do not need: **this one costs a
 * model call.** Every other view in the panel is a fetch, and switching tab away
 * and back is free. A summary that re-ran on each render would spend real money
 * on a paragraph the reader has already read.
 *
 * So the cache is keyed on the *question*, not on the symbol alone: change the
 * interval or add an indicator and it is a different question, deserving a
 * different answer. Everything else — switching tab, re-opening the panel,
 * detaching the window — hits the cache.
 *
 * `invalidate` is what the Re-run control calls. It exists because a note is a
 * judgement rather than a fact: a reader who disagrees with one should be able
 * to ask again, and a reader watching a stock move should be able to ask again
 * after it has.
 *
 * ## Two caches, because the key is not known until after an await
 *
 * The question the cache is keyed on includes the chart, and reading the chart
 * is **async** — `chartSnapshot` waits for `nextStateRender` so the PNG is not
 * blank. That await sat before the cache lookup, so every caller that arrived
 * during it found the cache empty and started its own model call. StrictMode
 * double-mounts the effect, so the *first* open already did this; clicking the
 * tab two or three times ran two or three summaries and kept the last.
 *
 * So there is a second map, `inFlight`, keyed on what is known **synchronously**
 * — the symbol and the chart group — and set before anything is awaited. It
 * collapses concurrent callers onto one promise; `cache` still holds the
 * resolved answers, keyed on the full question, and is what makes re-opening
 * the panel free.
 */

import { useEffect, useRef, useState } from "react";

import { fetchSummary, type SummaryResult } from "../api/analyst";
import { chartSnapshot, type ChartSnapshot } from "./chart-snapshot";

/** Resolved answers, keyed on the whole question. Survives the call. */
const cache = new Map<string, Promise<SummaryResult>>();

/**
 * Calls currently running, keyed on what is known before the first await.
 *
 * Not a cache: an entry lives only as long as its request, so it dedupes
 * concurrent callers and never serves a stale answer. `cache` does that.
 */
const inFlight = new Map<string, Promise<SummaryResult>>();

/** The synchronous half of the key — symbol and chart, before either is read. */
function gate(symbol: string, chartGroupId: string | undefined): string {
  return `${symbol.toUpperCase()}|${chartGroupId ?? "-"}`;
}

/**
 * The cache key, and why the image is not in it.
 *
 * The interval and the indicator set are the reader's *questions*; the PNG is a
 * picture of the same question a few pixels different, and keying on its bytes
 * would miss every time — a chart redraws on every bar, every resize and every
 * hover.
 */
function key(symbol: string, chart: ChartSnapshot | undefined): string {
  if (!chart) return `${symbol.toUpperCase()}|no-chart`;
  return `${symbol.toUpperCase()}|${chart.interval}|${[...chart.indicators].sort().join(",")}`;
}

/**
 * `asOf` asks for a past note from the "As of" list: a read, never a model call,
 * so it has its own cache and gate entries — picking last week's note must not
 * be collapsed onto, or replace, the latest one.
 */
export function loadSummary(
  symbol: string,
  chartGroupId?: string,
  refresh = false,
  asOf?: string,
): Promise<SummaryResult> {
  if (!fetchSummary) {
    return Promise.resolve({
      ok: false,
      error: { message: "QueVee is not configured, so no summary can be written." },
    });
  }

  // Deliberately **not** async: everything up to here must run in one
  // uninterrupted turn, so a second caller cannot arrive between the lookup and
  // the set. That is the whole fix — the old body awaited the chart first, and
  // every click during those milliseconds started its own model call.
  const id = gate(symbol, chartGroupId) + (asOf ? `|@${asOf}` : "");
  const running = inFlight.get(id);
  if (running) return running;

  const started = request(fetchSummary, symbol, chartGroupId, refresh, asOf);
  inFlight.set(id, started);

  // Cleared when it settles, either way — a failed call must not leave a gate
  // that refuses every later attempt. Compared by identity because a Re-run
  // during this call replaces the entry, and this one finishing afterwards must
  // not evict the newer call's.
  const clear = (): void => {
    if (inFlight.get(id) === started) inFlight.delete(id);
  };
  void started.then(clear, clear);
  return started;
}

/** `send` is passed in already narrowed — `loadSummary` is where it is checked. */
async function request(
  send: NonNullable<typeof fetchSummary>,
  symbol: string,
  chartGroupId: string | undefined,
  refresh = false,
  asOf?: string,
): Promise<SummaryResult> {
  // The snapshot decides which question this is — and it is cheap in work but
  // not instant in time: `captureImage` waits a frame so the PNG is not blank.
  const chart = await chartSnapshot(chartGroupId);
  const id = key(symbol, chart) + (asOf ? `|@${asOf}` : "");
  const existing = cache.get(id);
  if (existing) return existing;

  const pending = send(symbol, {
    interval: chart?.interval ?? "1d",
    barsShown: chart?.barsShown ?? 0,
    indicators: chart?.indicators ?? [],
    latest: chart?.latest ?? {},
    closePath: chart?.closePath ?? [],
    ...(chart?.windowLow !== undefined ? { windowLow: chart.windowLow } : {}),
    ...(chart?.windowHigh !== undefined ? { windowHigh: chart.windowHigh } : {}),
    ...(chart?.windowChange !== undefined ? { windowChange: chart.windowChange } : {}),
    ...(chart?.imagePng ? { imagePng: chart.imagePng } : {}),
  }, refresh, asOf).then((result) => {
    // A failure is not cached — a service that was down when the tab opened
    // should answer on the next try rather than after a reload. That is
    // `symbol-index.ts`'s rule, and it matters more here: the failure a reader
    // is most likely to hit is a QueVee that had not started yet.
    if (!result.ok) cache.delete(id);
    return result;
  });
  cache.set(id, pending);
  return pending;
}

/** Forget one symbol's notes, at every interval. What Re-run calls. */
export function invalidateSummary(symbol: string): void {
  const prefix = `${symbol.toUpperCase()}|`;
  for (const id of [...cache.keys()]) {
    if (id.startsWith(prefix)) cache.delete(id);
  }
  // The in-flight gate too, or Re-run pressed during a call would hand back the
  // very call the reader is asking to replace.
  for (const id of [...inFlight.keys()]) {
    if (id.startsWith(prefix)) inFlight.delete(id);
  }
}

/** Forget everything. For tests. */
export function invalidateSummaries(): void {
  cache.clear();
  inFlight.clear();
}

export interface SummaryState {
  /** True while a call is in flight — what renders as "Thinking…". */
  thinking: boolean;
  /**
   * Seconds spent so far, while thinking.
   *
   * **Measured: 95% of a summary is the model writing.** Fetching the company
   * from qv-data is under a second; Claude then produces about a thousand
   * tokens of analysis at ~58 a second. That wait cannot be argued down while
   * the note stays this long — but a static word for nineteen seconds reads as
   * a hang, and the same nineteen seconds with a number climbing reads as work.
   */
  elapsed: number;
  result: SummaryResult | undefined;
  /** True while a past note picked from the "As of" list is loading — no model call. */
  picking: boolean;
  /**
   * Ask again. `refresh` writes a new shared note — honoured for an admin any
   * time and for a member once the latest is 15 minutes old.
   */
  rerun: (refresh?: boolean) => void;
  /** Show the note written at `writtenAt`; `undefined` goes back to the latest. */
  pick: (writtenAt: string | undefined) => void;
}

/**
 * React binding. `thinking` until the first answer lands.
 *
 * The symbol is captured per effect run and checked on resolution, so switching
 * ticker mid-flight cannot paint one company's thesis under another's heading.
 * That guard matters more here than on the numeric views: a wrong P/E is
 * obviously wrong, and a wrong *thesis* is fluent and plausible.
 */
export function useSummary(
  symbol: string,
  chartGroupId?: string,
  enabled = true,
): SummaryState {
  const [result, setResult] = useState<SummaryResult | undefined>(undefined);
  const [thinking, setThinking] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [nonce, setNonce] = useState(0);
  // Whether the next load forces a new shared note. Set by an admin's Refresh,
  // consumed by the load it triggers, so a plain re-render never forces one.
  const force = useRef(false);
  // The past note the next load asks for, set by `pick`; kept across renders so
  // a re-render shows the same note rather than jumping back to the latest.
  const picked = useRef<string | undefined>(undefined);
  const pickedFor = useRef(symbol);

  useEffect(() => {
    if (!enabled) return;
    let live = true;
    setResult(undefined);
    setThinking(true);
    setElapsed(0);

    // A whole second, not an animation frame: this is a nineteen-second wait
    // and the reader is being told roughly where they are in it, not watching a
    // stopwatch. Sixty repaints a second for that would be sixty times the cost
    // of the information.
    const started = Date.now();
    const tick = setInterval(() => {
      if (live) setElapsed(Math.round((Date.now() - started) / 1000));
    }, 1000);

    // A date picked for one symbol means nothing for the next.
    if (pickedFor.current !== symbol) picked.current = undefined;
    pickedFor.current = symbol;
    const refresh = force.current;
    force.current = false;
    const asOf = refresh ? undefined : picked.current;
    void loadSummary(symbol, chartGroupId, refresh, asOf).then((answer) => {
      if (!live) return;
      setResult(answer);
      setThinking(false);
    });
    return () => {
      live = false;
      clearInterval(tick);
    };
  }, [symbol, chartGroupId, enabled, nonce]);

  return {
    thinking,
    elapsed,
    result,
    picking: thinking && picked.current !== undefined,
    rerun: (refresh = false) => {
      invalidateSummary(symbol);
      force.current = refresh;
      picked.current = undefined;
      setNonce((previous) => previous + 1);
    },
    pick: (writtenAt) => {
      picked.current = writtenAt;
      setNonce((previous) => previous + 1);
    },
  };
}
